import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import {
  BOMItemModel,
  defaultNPD,
  NPDModel,
  NPDType,
} from "../../models/npd_model";
import { fetchGet, fetchPost, fetchPut } from "../../../../service/api_client";
import { urls } from "../../../../utils/urls";
import ProjectSearchFieldV2 from "../../../purchase_order/purchase_order_create/components/project_search_widget";
import BOMItemComponent from "./bom_item_component_widget";
import ModalDialog from "../../../../ui/new_components/common/modal";
import ProductSearchField from "./productSeachField";
import { mdiArrowRight, mdiChevronLeft } from "@mdi/js";
import Icon from "@mdi/react";
import "../../../../styles/responsive_shrink.css";

interface NPDProps {}

const NPDCreateScreen: FunctionComponent<NPDProps> = () => {
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd`;
  const [, setLoading] = useState(false);
  const [addNewId1, setAddNewId1] = useState(Math.random() * 1000);
  const { showToast } = useToast();
  const [screen, setScreen] = useState("npd_details");

  const [data, setData] = useState<NPDModel>({
    ...defaultNPD,
  });
  const existingItems = data?.bom_items || [];
  const [showNonExistItemsModal, setShowNonExistItemsModal] = useState(false);
  const [nonExistItems, setNonExistItems] = useState<string[]>([]);

  const navigate = useHistory();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const upload_url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/import-items`;

    try {
      const res = await fetchPost(upload_url, formData);

      if (res.success) {
        setData((o: NPDModel) => {
          const combined = [...o.bom_items, ...res.data];

          const seen = new Set<string>();
          const deduped = combined.filter((item) => {
            const key = `${item.warehouse.id}::${item.item_code}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

          return {
            ...o,
            bom_items: deduped,
          };
        });

        showToast({ type: ToastType.success, text: res.message });
      } else {
        if (res.non_existent_items?.length > 0) {
          setNonExistItems(res.non_existent_items);
          setShowNonExistItemsModal(true);
        }
        showToast({ type: ToastType.error, text: res.message });
      }
    } catch (err) {
      showToast({ type: ToastType.error, text: "File upload failed." + err });
      console.error(err);
    } finally {
      // Clear the file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ["Warehouse", "Item Code", "Quantity"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "bom_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (id) getVoucher(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getVoucher = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + "/" + id);
      if (res.success) {
        setData((prevData) => ({
          ...prevData,
          ...res.data,
        }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "Failed to fetch voucher" });
    }
    setLoading(false);
  };

  const submit = async () => {
    if (
      !data?.product?.name &&
      !data?.project?.name &&
      !data.type &&
      !data.qty
    ) {
      return showToast({
        type: ToastType.error,
        text: "Please add required fields!!!",
      });
    }
    setLoading(true);
    const res = id
      ? await fetchPut(url + "/" + id, data)
      : await fetchPost(url, data);
    if (res.success) {
      showToast({ type: ToastType.success, text: res.message });
      navigate.push(urls.npd);
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  return (
    <>
      {showNonExistItemsModal && (
        <ModalDialog
          show={showNonExistItemsModal}
          title="Items Not Exists"
          onClose={() => setShowNonExistItemsModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              <div className="modal-content">
                <h2>Rejected Upload Items</h2>
                <ul>
                  {nonExistItems.map((id: any, index: any) => (
                    <li key={index}>{id}</li>
                  ))}
                </ul>
              </div>
            </pre>
          </div>
        </ModalDialog>
      )}
      <div className="mobile-shrink sticky top-0 mt-2 px-2 md:px-6 h-full">
        <div className="flex items-center mb-2">
          <button
            className="  flex  items-center px-2 hover:text-gray-700  font-bold text-lg"
            onClick={() => {
              navigate.goBack();
            }}
          >
            <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold"></Icon>{" "}
            Back
          </button>
          <div className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
            NPD
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-10 mb-2">
          <button
            className={`text-lg font-bold pl-2 ${
              screen === "npd_details"
                ? "bg-myPrimaryColor text-white rounded-md p-1"
                : "text-myPrimaryColor"
            }`}
            onClick={() => {
              setScreen("npd_details");
            }}
          >
            NPD Details
          </button>

          <button
            className={`text-lg font-bold pl-2 ${
              screen === "BOM"
                ? "bg-myPrimaryColor text-white rounded-md p-1"
                : "text-myPrimaryColor"
            }`}
            onClick={() => {
              setScreen("BOM");
            }}
          >
            BOM
          </button>
        </div>
        {screen === "npd_details" && (
          <div>
            <div className="bg-white rounded p-4 md:p-8 my-4 md:my-10 flex gap-1 h-auto overflow-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10 p-2 md:p-5 text-sm w-full lg:w-3/4 font-semibold">
                <div className="">
                  <div className="flex flex-col gap-1">
                    <label className="text-md pt-1 font-semibold">
                      Project *
                    </label>
                    <ProjectSearchFieldV2
                      disabled={false} // Always editable
                      placeholder="Select Project"
                      value={data.project}
                      onSelect={(d) => {
                        setData((prevData) => ({
                          ...prevData,
                          project: {
                            ...prevData.project!,
                            name: d.name,
                            description: d.description,
                          },
                        }));
                      }}
                    />
                  </div>
                  <div className="my-2">
                    <label htmlFor="" className="text-sm font-semibold">
                      Product Name *
                    </label>{" "}
                    <ProductSearchField
                      //   disabled={true}
                      value={data?.product}
                      onSelect={(d) => {
                        if (d.type === "Product") {
                          setData((o) => ({
                            ...o,
                            product: d,
                            product_id: d.id,
                          }));
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="">
                  <div className="flex flex-col gap-1">
                    <label className="text-md pt-1 font-semibold">
                      {" "}
                      Type *
                    </label>
                    <select
                      value={data.type}
                      onChange={(e) => {
                        setData((old) => {
                          return {
                            ...old,
                            type: e.target.value,
                          };
                        });
                      }}
                      name=""
                      id="type"
                      className="p-1 rounded  focus:outline-none w-full border"
                    >
                      <option value="" hidden>
                        Select
                      </option>

                      {Object.values(NPDType).map((c, i) => (
                        <option value={c} key={i}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-md pt-1 font-semibold"> Qty *</label>
                    <input
                      type="text"
                      required
                      className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                      value={data.qty}
                      onChange={(e) => {
                        setData((old) => {
                          return {
                            ...old,
                            qty: Number(e.target.value),
                          };
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end items-center">
              <div
                onClick={() => {
                  setScreen("BOM");
                }}
                className="bg-myPrimaryColor text-white rounded-full cursor-pointer hover:scale-110 transform duration-100"
              >
                <Icon path={mdiArrowRight} className="h-8 w-10 p-2 "></Icon>
              </div>
            </div>
          </div>
        )}
        {screen === "BOM" && (
          <div>
            <div className="bg-white rounded p-3 md:p-8 my-4 md:my-10 h-auto overflow-auto w-full">
              <div className="mt-3 w-full">
                <div className="w-full">
                  <div className="w-full">
                    <div className="flex flex-wrap gap-2 items-center justify-between">
                      <h3 className="text-sm font-bold">Items</h3>
                      <div className="">
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept=".csv"
                          onChange={handleCSVUpload}
                          className="hidden"
                        />

                        {/* <label id="csvUpload"> */}
                        <label
                          onClick={handleDownloadTemplate}
                          className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
                        >
                          Template
                        </label>
                        <button
                          onClick={handleButtonClick}
                          className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1"
                        >
                          Upload BOM
                        </button>
                        {/* </label> */}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 pb-24">
                          {data.bom_items.map((comp, i) => {
                            return (
                              <BOMItemComponent
                                data={comp}
                                edit={false}
                                disabled={false}
                                key={comp.id}
                                onDelete={(d: any) => {
                                  setData((o) => ({
                                    ...o,
                                    bom_items: [...(o.bom_items || [])].filter(
                                      (i) => i.id !== d.id,
                                    ),
                                  }));
                                }}
                                onSubmit={(data: BOMItemModel): void => {
                                  setData((o: NPDModel) => ({
                                    ...o,
                                    bom_items: [
                                      ...(o.bom_items || []).map((it) => {
                                        if (it.id === data.id) return data;
                                        return it;
                                      }),
                                    ],
                                  }));
                                }}
                                existingItems={existingItems}
                              />
                            );
                          })}

                          {
                            <BOMItemComponent
                              key={addNewId1}
                              onSubmit={function (data: BOMItemModel): void {
                                setData((o: NPDModel) => ({
                                  ...o,
                                  bom_items: [...(o.bom_items || []), data],
                                }));
                                setAddNewId1(Math.random() * 1000);
                              }}
                              // index={i}
                              edit={true}
                              disabled={false}
                              existingItems={existingItems}
                            />
                          }
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 z-10 bg-white border-t flex flex-wrap gap-2 items-center justify-between px-2 md:px-5 py-3">
              <button
                className="text-md text-myPrimaryColor font-bold pl-2 "
                onClick={() => {
                  navigate.push(urls.npd + "/view/" + id);
                }}
              >
                Final Review & Submit
              </button>
              <div className="flex flex-wrap justify-end gap-2 md:gap-4">
                <button
                  type="button"
                  className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
                  onClick={submit}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
                  onClick={() => navigate.push(urls.npd)}
                >
                  Quit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NPDCreateScreen;
